import React, { useState, useEffect, useRef } from "react";

/* FROZEN — end of Phase: Shared-Surface Discovery (Feb 2026).
   Study 3 (jointly revised synthesis) is preserved as a stable
   artifact of this phase. Do not modify. If future studies
   contradict its behavior, create a new route rather than
   editing this one. The paired backend endpoint at
   /app/backend/sonmi_coauthor_study.py is frozen alongside. */

/**
 * Vessel — Co-authored synthesis study (study 3).
 *
 * Tests whether the final understanding can be genuinely
 * co-authored — SONMI composes a provisional synthesis from the
 * steward's actual entered language, the steward can revise that
 * synthesis through natural language (not editing), and the
 * surface reorganizes each time until the steward expresses
 * completion in language rather than a click.
 *
 * Same visual restraint as study 2:
 *   - both parties in Outfit,
 *   - 1.5px vertical provenance marks (amber for SONMI, stone for
 *     the steward),
 *   - no coloured Vessel region,
 *   - Simone as a peripheral reference,
 *   - the drop remains in the shared surface.
 *
 * What is new here:
 *   - Real LLM co-composition via /api/sonmi/study/co-author.
 *   - Multiple revision cycles — the steward may reshape the
 *     synthesis as many times as they need.
 *   - Provisionality is expressed only through type-weight and
 *     colour (stone-700 at 0.92 opacity), never through badges,
 *     labels, or explanatory text.
 *   - Completion is detected from natural language (`"yes, that's it"`
 *     / `"keep it"` / `"that is what I mean"`), not from a button.
 *   - When the synthesis reforms, the previous synthesis loosens
 *     upward like SONMI's wrong clause did in study 2, and the new
 *     synthesis gathers from below.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SONMI_OPENING_VALID = "She has been pausing before schedule changes lately.";
const SONMI_OPENING_WRONG = "I wonder if the new rhythm is unsettling her.";

// Ghost placeholder — an invitation, not a script. Steward may
// type anything.
const GHOST_FIRST_TURN = "Not quite. She isn't uncertain — she's checking how the change will affect everyone else.";
const GHOST_REVISION = "Close, but I wouldn't call it protectiveness — it is more like responsibility.";

function newSessionId() {
  return "s_" + Math.random().toString(36).slice(2, 10);
}

export default function VesselCoAuthored() {
  const [phase, setPhase] = useState("env"); // env | sonmi_arrive | co_author | settling | dissolve | after
  const [sessionId] = useState(newSessionId);
  const [history, setHistory] = useState([]); // [{author, text}]
  const [currentSynthesis, setCurrentSynthesis] = useState(null);
  const [prevSynthesis, setPrevSynthesis] = useState(null); // during reforming
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);
  const [stewardText, setStewardText] = useState("");
  const [turnIndex, setTurnIndex] = useState(0); // to reset the contenteditable
  const [wrongClauseLoose, setWrongClauseLoose] = useState(false);
  const editRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (phase === "sonmi_arrive") {
      timerRef.current = setTimeout(() => setPhase("co_author"), 1400);
    } else if (phase === "settling") {
      timerRef.current = setTimeout(() => setPhase("dissolve"), 900);
    } else if (phase === "dissolve") {
      timerRef.current = setTimeout(() => setPhase("after"), 1300);
    }
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [phase]);

  // Focus editable when it becomes the steward's turn.
  useEffect(() => {
    if (phase === "co_author" && !waiting && editRef.current) {
      setTimeout(() => editRef.current && editRef.current.focus(), 80);
    }
  }, [phase, waiting, turnIndex]);

  // Clear the previous synthesis after the reform transition finishes.
  useEffect(() => {
    if (!prevSynthesis) return;
    const t = setTimeout(() => setPrevSynthesis(null), 1100);
    return () => clearTimeout(t);
  }, [prevSynthesis]);

  const submitSteward = async () => {
    if (waiting) return;
    const text = (stewardText || "").trim();
    if (!text) return; // no ghost fallback here — the steward must speak

    // Append to history for the request; the on-screen history is
    // just the latest steward and the current synthesis.
    const nextHistory = [...history, { author: "steward", text }];
    setHistory(nextHistory);
    setStewardText("");
    if (editRef.current) editRef.current.textContent = "";
    setWaiting(true);
    setError(null);

    // On the FIRST steward turn, register the disagreement by
    // loosening SONMI's wrong clause immediately — so the visible
    // consequence of speaking arrives before the LLM answers.
    if (turnIndex === 0) setWrongClauseLoose(true);

    try {
      const r = await fetch(`${BACKEND_URL}/api/sonmi/study/co-author`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          sonmi_valid: SONMI_OPENING_VALID,
          history: nextHistory.slice(0, -1), // exclude the just-added steward turn — the endpoint receives it as `new_steward`
          new_steward: text,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (data.action === "complete") {
        setWaiting(false);
        setPhase("settling");
        return;
      }
      // action === "synthesize"
      if (currentSynthesis) setPrevSynthesis(currentSynthesis);
      setCurrentSynthesis(data.synthesis);
      setHistory((h) => [...h, { author: "sonmi", text: data.synthesis }]);
      setWaiting(false);
      setTurnIndex((i) => i + 1);
    } catch (e) {
      setWaiting(false);
      setError(String(e.message || e));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitSteward();
    }
  };

  const reset = () => {
    setPhase("env");
    setHistory([]);
    setCurrentSynthesis(null);
    setPrevSynthesis(null);
    setWaiting(false);
    setError(null);
    setStewardText("");
    setTurnIndex(0);
    setWrongClauseLoose(false);
    if (editRef.current) editRef.current.textContent = "";
  };

  const showVessel = phase === "sonmi_arrive" || phase === "co_author" || phase === "settling";
  const settled = phase === "settling" || phase === "dissolve" || phase === "after";

  const ghost = turnIndex === 0 ? GHOST_FIRST_TURN : GHOST_REVISION;

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{ backgroundColor: "#FAFAF7", fontFamily: "'Outfit', sans-serif" }}
      data-testid="physics-coauthored-root"
    >
      <div
        className="absolute top-6 left-8 text-[10px] uppercase tracking-[0.18em] text-stone-400"
        data-testid="study-label"
      >
        SONMI · physics study · co-authored synthesis · placeholder content
      </div>

      {phase === "after" && (
        <button
          onClick={reset}
          data-testid="reset"
          className="absolute top-6 right-8 text-[11px] italic text-stone-400 hover:text-stone-700 transition-colors"
        >
          watch again
        </button>
      )}

      {/* Simone — peripheral reference, unchanged across states. */}
      <div
        className="absolute"
        style={{ left: "9%", top: "50%", transform: "translateY(-50%)" }}
        data-testid="subject-simone"
      >
        <div className="flex flex-col items-center">
          <div
            className="rounded-full bg-stone-100 border border-stone-100 flex items-center justify-center text-stone-500"
            style={{ width: 48, height: 48, fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 18 }}
          >
            SC
          </div>
          <div className="mt-2 text-[12px] text-stone-500" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            Simone
          </div>
        </div>
      </div>

      {/* Invitation — visible only in the still environment. */}
      {phase === "env" && (
        <button
          onClick={() => setPhase("sonmi_arrive")}
          data-testid="invitation"
          className="absolute text-[13px] italic text-stone-400 hover:text-stone-700 transition-colors"
          style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
        >
          sit here with something about simone
        </button>
      )}

      {showVessel && (
        <div
          className="absolute"
          style={{ left: "32%", top: "16%", right: "12%", bottom: "16%" }}
          data-testid="vessel-contents"
        >
          {/* SONMI's opening — two clauses. Same structural pattern as study 2. */}
          <div
            className="absolute transition-all duration-1200"
            style={{
              top: "0%",
              left: 0,
              right: "8%",
              opacity: phase === "sonmi_arrive" ? 0.35 : 1,
              transform: phase === "sonmi_arrive" ? "translateY(10px)" : "translateY(0)",
            }}
            data-testid="sonmi-opening"
          >
            <div className="flex gap-4">
              <ProvenanceMark author="sonmi" />
              <div
                className="text-stone-800"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 300,
                  fontSize: "1.08rem",
                  lineHeight: 1.6,
                  maxWidth: "540px",
                }}
              >
                <span data-testid="sonmi-clause-valid">{SONMI_OPENING_VALID}</span>{" "}
                <span
                  data-testid="sonmi-clause-loose"
                  className="transition-all duration-1400"
                  style={{
                    display: "inline-block",
                    opacity: wrongClauseLoose ? 0 : 1,
                    transform: wrongClauseLoose ? "translateY(-10px) scale(0.98)" : "translateY(0) scale(1)",
                    color: wrongClauseLoose ? "rgba(120,113,108,0.4)" : undefined,
                  }}
                >
                  {SONMI_OPENING_WRONG}
                </span>
              </div>
            </div>
          </div>

          {/* Steward's contributions (history) + current input.
              Rendered as a single vertical flow starting just below
              SONMI's opening. Only the most recent steward turn is
              shown; earlier ones are absorbed into the synthesis. */}
          {phase === "co_author" && (
            <div className="absolute" style={{ top: "22%", left: 0, right: "8%" }} data-testid="steward-zone">
              {/* Previous steward turn (if any) — shown quietly above the current input,
                  visibly authored but no longer active. */}
              {(() => {
                const lastSteward = [...history].reverse().find((h) => h.author === "steward");
                if (!lastSteward || !currentSynthesis) return null;
                return (
                  <div className="flex gap-4 mb-6 opacity-70" data-testid="last-steward">
                    <ProvenanceMark author="steward" />
                    <div
                      className="text-stone-500"
                      style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontSize: "0.95rem", lineHeight: 1.55, maxWidth: "540px" }}
                    >
                      {lastSteward.text}
                    </div>
                  </div>
                );
              })()}

              {/* The current steward input — always present unless waiting. */}
              {!waiting && (
                <div className="flex gap-4 items-start" data-testid="steward-input-row">
                  <ProvenanceMark author="steward" />
                  <div style={{ maxWidth: "540px", width: "100%" }}>
                    <div
                      key={turnIndex}
                      ref={editRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={(e) => setStewardText(e.currentTarget.textContent)}
                      onKeyDown={handleKeyDown}
                      data-testid="steward-input"
                      data-placeholder={ghost}
                      className="steward-input outline-none text-stone-800 caret-amber-600"
                      style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 300,
                        fontSize: "1.05rem",
                        lineHeight: 1.6,
                        minHeight: "1.6em",
                      }}
                    />
                    {turnIndex === 0 && (
                      <div className="text-[10px] italic text-stone-300 mt-2" data-testid="steward-hint">
                        your marker — press <span className="not-italic tracking-wider">↵</span> to leave your thought on the surface
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quiet waiting mark — a barely-perceptible amber dot
                  pulsing gently where the synthesis will materialize.
                  No spinner, no "thinking..." label. The steward's
                  just-submitted turn is already visible via the
                  "last-steward" block above, so we don't echo it. */}
              {waiting && (
                <div className="flex gap-4 items-start" data-testid="waiting">
                  <ProvenanceMark author="sonmi" />
                  <div className="mt-1 flex items-center gap-3">
                    <span
                      className="rounded-full"
                      style={{
                        width: 5,
                        height: 5,
                        backgroundColor: "#d97706",
                        opacity: 0.55,
                        animation: "sonmi-thinking-pulse 1600ms ease-in-out infinite",
                      }}
                      data-testid="thinking-pulse"
                    />
                    <span className="text-[11px] italic text-stone-300">gathering</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* The provisional synthesis. No label. No badge. Its
              provisionality is expressed only through slightly
              softer color (stone-700) and slightly reduced opacity
              (0.92) — until the steward completes, at which point
              it settles: colour deepens to stone-900, opacity to 1.
              Position also shifts down slightly on settling. */}
          {currentSynthesis && (
            <div
              className="absolute transition-all duration-900"
              style={{
                bottom: settled ? "8%" : "10%",
                left: 0,
                right: "8%",
                opacity: 1,
              }}
              data-testid="synthesis-zone"
            >
              {/* Previous synthesis loosening upward during a reform. */}
              {prevSynthesis && (
                <div
                  className="absolute transition-all duration-1000"
                  style={{
                    bottom: "100%",
                    left: 0,
                    right: 0,
                    opacity: 0,
                    transform: "translateY(-14px)",
                    animation: "sonmi-synth-loosen 1000ms ease-out forwards",
                  }}
                  data-testid="synthesis-loosening"
                >
                  <div
                    className="text-stone-500"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 400,
                      fontSize: "1.22rem",
                      lineHeight: 1.5,
                      maxWidth: "620px",
                    }}
                  >
                    {prevSynthesis}
                  </div>
                </div>
              )}
              <div
                key={currentSynthesis}
                className="transition-all duration-900"
                style={{
                  color: settled ? "#1c1917" : "#44403c",
                  opacity: settled ? 1 : 0.92,
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: settled ? 400 : 300,
                  fontSize: "1.24rem",
                  lineHeight: 1.5,
                  maxWidth: "620px",
                  animation: "sonmi-synth-gather 900ms ease-out",
                }}
                data-testid="synthesis-current"
              >
                {currentSynthesis}
              </div>
            </div>
          )}

          {error && (
            <div className="absolute bottom-0 left-0 right-0 text-[11px] text-red-400" data-testid="error">
              {error}
            </div>
          )}
        </div>
      )}

      {/* Dissolution trace — same as study 2: drop in the shared
          surface, not on Simone. */}
      {(phase === "dissolve" || phase === "after") && (
        <div
          className="absolute"
          style={{ left: "56%", top: "50%", transform: "translate(-50%, -50%)" }}
          data-testid="remaining-trace-wrapper"
        >
          <span
            className="absolute pointer-events-none rounded-full"
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 32,
              height: 32,
              background: "radial-gradient(circle, rgba(217,119,6,0.22) 0%, rgba(217,119,6,0.04) 55%, transparent 78%)",
              animation: "sonmi-drop-aura 900ms cubic-bezier(0.16, 0.68, 0.44, 1) forwards",
            }}
            data-testid="remaining-trace-aura"
          />
          <span
            className="rounded-full block"
            style={{
              width: 5,
              height: 5,
              backgroundColor: "#d97706",
              opacity: 0.75,
              animation: "sonmi-drop-arrive 700ms cubic-bezier(0.16, 0.68, 0.44, 1) forwards",
            }}
            data-testid="remaining-trace-drop"
          />
        </div>
      )}

      <style>{`
        .steward-input:empty:before {
          content: attr(data-placeholder);
          color: rgba(120, 113, 108, 0.42);
          font-style: italic;
          pointer-events: none;
        }
        @keyframes sonmi-drop-arrive {
          0%   { transform: scale(0);   opacity: 0; }
          38%  { transform: scale(1.45); opacity: 0.9; }
          70%  { transform: scale(0.88); opacity: 0.72; }
          100% { transform: scale(1);   opacity: 0.75; }
        }
        @keyframes sonmi-drop-aura {
          0%   { transform: translate(-50%, -50%) scale(0);   opacity: 0; }
          35%  { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(0.55); opacity: 0; }
        }
        @keyframes sonmi-thinking-pulse {
          0%,100% { opacity: 0.25; transform: scale(0.9); }
          50%     { opacity: 0.7;  transform: scale(1.1); }
        }
        @keyframes sonmi-synth-gather {
          0%   { opacity: 0;    transform: translateY(10px); }
          100% { opacity: 0.92; transform: translateY(0); }
        }
        @keyframes sonmi-synth-loosen {
          0%   { opacity: 0.6; transform: translateY(0); }
          100% { opacity: 0;   transform: translateY(-14px); }
        }
      `}</style>
    </div>
  );
}

function ProvenanceMark({ author }) {
  const color = author === "sonmi" ? "#d97706" : "#a8a29e";
  return (
    <span
      aria-hidden="true"
      data-testid={`provenance-${author}`}
      className="inline-block flex-shrink-0"
      style={{ width: 1.5, height: 18, backgroundColor: color, marginTop: 6, opacity: author === "sonmi" ? 0.85 : 0.7 }}
    />
  );
}
